<template>
  <transition name="qs-float">
    <div
      v-if="isVisible"
      :class="pinned ? 'qs-pinned' : ['qs-floating', dragClasses]"
      :style="pinned ? undefined : dragStyle"
    >
      <!-- Main container - Compact single-row layout -->
      <div class="qs-container">
        <!-- Left: Drag handle zone (only in floating mode) -->
        <div v-if="!pinned" class="qs-drag-zone" @mousedown="startDrag">
          <v-icon size="16" class="drag-icon">mdi-drag-vertical</v-icon>
        </div>

        <!-- Center: Icon strip -->
        <div class="qs-icon-strip">
          <!-- Tags group - Hierarchical buttons (have children, support right-click overlay) -->
          <div class="qs-icon-group">
            <QuickSettingsIcon
              v-for="item in hierarchicalTagItems"
              :key="item.id"
              :tag-id="item.id"
              :icon="item.icon"
              :label="item.label"
              :tag-color="item.color"
              :state="getItemState(item)"
              :disabled="item.disabled"
              :has-children="!!item.children"
              @click="handleTagLeftClick(item, $event)"
              @contextmenu="handleTagRightClick(item, $event)"
            />
          </div>

          <!-- Subtle divider between hierarchical and simple tag buttons -->
          <div class="qs-divider qs-divider--subtle" />

          <!-- Tags group - Simple buttons (no children, just toggle flags) -->
          <div class="qs-icon-group">
            <QuickSettingsIcon
              v-for="item in simpleTagItems"
              :key="item.id"
              :tag-id="item.id"
              :icon="item.icon"
              :label="item.label"
              :tag-color="item.color"
              :state="getItemState(item)"
              :disabled="item.disabled"
              @click="handleTagLeftClick(item, $event)"
            />
          </div>

          <!-- Divider before view items -->
          <div class="qs-divider" />

          <!-- View group -->
          <div class="qs-icon-group">
            <QuickSettingsIcon
              v-for="item in viewItems"
              :key="item.id"
              :icon="item.icon"
              :label="item.label"
              :active="item.active"
              :disabled="item.disabled"
              :has-slider="item.hasSlider"
              :has-action="item.hasAction"
              @click="handleToggle(item, $event)"
              @slider-toggle="handleSliderToggle(item, $event)"
            />
          </div>
        </div>

        <!-- Actions group: Pin/Unpin (with visible divider before it in pinned mode) -->
        <div
          v-if="canPinToTopBar"
          class="qs-divider"
          :class="{ 'qs-divider--subtle': !pinned }"
        />
        <div v-if="canPinToTopBar" class="qs-actions-group">
          <!-- Pin / Unpin Button -->
          <v-tooltip bottom :open-delay="400">
            <template v-slot:activator="{ on, attrs }">
              <button
                class="qs-action-btn"
                :class="{ 'is-pinned-btn': isPinPreferenceActive }"
                v-bind="attrs"
                v-on="on"
                @click="togglePin"
                :aria-label="isPinPreferenceActive ? 'Unpin (Float bar)' : 'Pin to top bar'"
              >
                <v-icon :size="18">
                  {{ isPinPreferenceActive ? 'mdi-pin-off-outline' : 'mdi-pin-outline' }}
                </v-icon>
              </button>
            </template>
            <span class="qs-tooltip">
              {{ isPinPreferenceActive ? 'Unpin (Float bar)' : 'Pin to top bar' }}
            </span>
          </v-tooltip>
        </div>

      </div>

      <!-- Slider Overlay -->
      <SliderOverlay
        v-if="activeSlider"
        :value="sliderValue"
        :label="activeSlider.label"
        :unit="activeSlider.unit"
        :min="activeSlider.min"
        :max="activeSlider.max"
        :log-scale="activeSlider.logScale"
        :position="sliderPosition"
        @input="handleSliderInput"
        @close="closeSlider"
      />

      <!-- Tag Sub-Items Overlay (for parent buttons with children) -->
      <TagSubItemsOverlay
        :visible="!!activeSubItems"
        :parent-label="activeSubItems?.label"
        :items="activeSubItems?.children || []"
        :position="subItemsPosition"
        :display-settings="displaySettings"
        @toggle="handleSubItemToggle"
        @toggle-all="handleSubItemToggleAll"
        @close="closeSubItemsOverlay"
      />

      <!-- Ghost-style overlay (wireframe / solid grey + colour + opacity) -->
      <GhostControlOverlay
        :visible="ghostOverlayOpen"
        :position="ghostOverlayPosition"
        :ghosting-on="isGhostingOn"
        :wireframe="ghostWireframe"
        :color="ghostColor"
        :opacity="ghostOpacity"
        :can-toggle="ghostHasIsolation"
        :auto-show="ghostAutoShow"
        @toggle-ghost="onGhostToggle"
        @set-wireframe="onGhostSetWireframe"
        @set-color="onGhostSetColor"
        @set-opacity="onGhostSetOpacity"
        @set-auto-show="onGhostSetAutoShow"
        @close="closeGhostOverlay"
      />
    </div>
  </transition>
</template>

<script>
import { mapGetters } from 'vuex';
import { setModelVisibility } from '@/utils/forgeUtils';
import { createDraggableMixin } from '@/mixins/draggableMixin';
import QuickSettingsIcon from './QuickSettingsIcon.vue';
import SliderOverlay from './SliderOverlay.vue';
import TagSubItemsOverlay from './TagSubItemsOverlay.vue';
import GhostControlOverlay from './GhostControlOverlay.vue';
import { readGhostAutoShow, writeGhostAutoShow } from './ghostOverlayPref';
import { hydrateCmlsWithTmlPoints, getInspectionCmls } from '@Tags/components/monitoring/useMonitoringTab';
import { budgetCmlsForMarkers } from '@Tags/components/monitoring/useLevelColoring';
import { NO_DATA_COLOR } from '@Tags/components/monitoring/cmlColoringConstants';

export default {
  name: 'QuickSettingsFloating',

  props: {
    pinned: {
      type: Boolean,
      default: false,
    },
  },

  mixins: [
    createDraggableMixin({
      storageKey: 'quick-settings-floating-position',
      defaultPosition: () => ({
        x: 100,
        y: 64,
      }),
      dialogDimensions: { width: 620, height: 52 },
      margin: 12,
      fullscreenProperty: null,
      zIndex: 100,
    }),
  ],

  components: {
    QuickSettingsIcon,
    SliderOverlay,
    TagSubItemsOverlay,
    GhostControlOverlay,
  },

  data() {
    return {
      activeSlider: null,
      sliderPosition: { x: 0, y: 0 },
      closedPointClouds: [],
      // Tag sub-items overlay state
      activeSubItems: null,
      subItemsPosition: { x: 0, y: 0 },
      // Ghost-style overlay state
      ghostOverlayOpen: false,
      ghostOverlayPosition: { x: 0, y: 0 },
      ghostAutoShow: true,
      // Live count of currently-isolated dbIds, polled while the overlay is open
      // (startIsolationPoll). Drives the On/Off gate.
      ghostIsolatedCount: 0,
    };
  },

  computed: {
    ...mapGetters('GENERAL', {
      isRailExpanded: 'GET_IS_RAIL_EXPANDED',
      totalNavWidth: 'GET_TOTAL_NAV_WIDTH',
      railWidth: 'GET_RAIL_WIDTH',
      arePanelsCollapsed: 'GET_ARE_PANELS_COLLAPSED',
      inspectionFilterDockInset: 'GET_INSPECTION_FILTER_DOCK_INSET',
    }),

    viewerAreaBounds() {
      const viewportWidth = this.$vuetify.breakpoint.width || window.innerWidth;
      const viewportHeight = this.$vuetify.breakpoint.height || window.innerHeight;
      const showNav = this.$store.getters['GET_SHOW_NAV_MENU'];
      let leftOffset = 0;
      if (showNav) {
        leftOffset = this.arePanelsCollapsed ? this.railWidth : this.totalNavWidth;
      }
      const rightOffset = this.inspectionFilterDockInset || 0;
      const topOffset = 48; // Top navigation bar height
      const bottomOffset = 0;
      const margin = 12;

      const barWidth = 620;
      const barHeight = 52;

      const minX = leftOffset + margin;
      const maxX = Math.max(minX, viewportWidth - rightOffset - barWidth - margin);
      const minY = topOffset + margin;
      const maxY = Math.max(minY, viewportHeight - bottomOffset - barHeight - margin);

      const defaultX = leftOffset + Math.max(margin, Math.round((viewportWidth - leftOffset - rightOffset - barWidth) / 2));
      const defaultY = topOffset + 16;

      return {
        leftOffset,
        rightOffset,
        topOffset,
        bottomOffset,
        minX,
        maxX,
        minY,
        maxY,
        defaultX,
        defaultY,
        margin,
      };
    },

    canPinToTopBar() {
      return this.$store.getters['GENERAL/GET_CAN_PIN_QUICK_SETTINGS_TO_TOP_BAR'];
    },

    isPinPreferenceActive() {
      return this.$store.getters['GENERAL/GET_IS_QUICK_SETTINGS_PINNED'];
    },

    isVisible() {
      return this.$store.getters['GENERAL/GET_IS_QUICK_SETTINGS_VISIBLE'];
    },

    forgeService() {
      return this.$store.state.forgeService;
    },

    viewer() {
      return this.$store.state.viewer;
    },

    displaySettings() {
      return this.$store.state.userSettings?.displaySettings;
    },

    pointCloudSettings() {
      return this.displaySettings?.pointCloudDisplay;
    },

    isModelLoaded() {
      return !this.$store.state.viewerState?.isLoading && this.$store.getters['GET_PAGE_WITH_TYPE_3D'];
    },

    pointClouds() {
      return this.$store.state.pointClouds || [];
    },

    shownPointClouds() {
      return this.pointClouds.filter(t => t.isShown);
    },

    // Tag items configuration - Hierarchical structure with color coding
    // Colors from TAG_COLOR_HEX in src/scripts/js/forgeModels.js
    tagItems() {
      return [
        // Operation parent button (purple) - has children overlay
        {
          id: 'operation',
          icon: 'mdi-clipboard-list',
          label: 'Operation',
          color: '#665b9b', // Purple
          hasOverlay: true,
          children: [
            { id: 'general', label: 'General', flag: 'showAnnotations', color: '#665b9b' },
            { id: 'scaffold', label: 'Scaffold', flag: 'showScaffolds', color: '#665b9b' },
            { id: 'permit', label: 'Permit', flag: 'showPermits', color: '#665b9b' },
            { id: 'container', label: 'Container', flag: 'showDeckManagment', color: '#665b9b' },
          ],
        },
        // Inspection parent button (maroon) - has children overlay
        {
          id: 'inspection',
          icon: 'mdi-clipboard-check',
          label: 'Inspection',
          color: '#9a5454', // Maroon
          hasOverlay: true,
          children: [
            { id: 'inspection-item', label: 'Inspection', flag: 'showInspections', color: '#9a5454' },
            { id: 'avaInspect', label: 'AVA Inspection', flag: 'showAvaInspect', color: '#61b695' },
            { id: 'anomaly', label: 'Anomaly', flag: 'showAnomalies', color: '#FF6347' },
            { id: 'cml', label: 'CML', flag: 'showCml', color: NO_DATA_COLOR },
            { id: 'tml', label: 'TML', flag: 'showTml', color: NO_DATA_COLOR },
          ],
        },
        // Equipment simple button (cyan) - toggles showTags + showAutoEquipment
        {
          id: 'equipment',
          icon: 'mdi-wrench',
          label: 'Equipment',
          color: '#00a8b7', // Cyan/Teal
          flags: ['showTags', 'showAutoEquipment'],
        },
        // Pipeline simple button (steel blue) - toggles showPipelines + showManualPipelines
        {
          id: 'pipeline',
          icon: 'mdi-pipe',
          label: 'Pipeline',
          color: '#547d9a', // Steel Blue
          flags: ['showPipelines', 'showManualPipelines'],
        },
        // Structural simple button (olive) - toggles showStructural + showManualStructural
        {
          id: 'structural',
          icon: 'mdi-bridge',
          label: 'Structural',
          color: '#5f6f64', // Olive/Sage
          flags: ['showStructural', 'showManualStructural'],
        },
      ];
    },

    // Hierarchical tag items (have children, support right-click overlay)
    hierarchicalTagItems() {
      return this.tagItems.filter(item => item.children);
    },

    // Simple tag items (no children, just toggle flags)
    simpleTagItems() {
      return this.tagItems.filter(item => !item.children);
    },

    // View items configuration
    viewItems() {
      const items = [
        {
          id: 'show360',
          icon: 'mdi-image-filter-center-focus',
          label: '360 Photos',
          active: this.displaySettings?.show360,
          toggle: 'show360',
        },
        {
          id: 'model',
          icon: 'mdi-cube',
          label: '3D Model',
          active: this.displaySettings?.showMasterModel,
          disabled: !this.isModelLoaded,
          toggle: 'showMasterModel',
        },
        {
          id: 'pointcloud',
          icon: 'mdi-grain',
          label: 'Point Clouds',
          active: this.shownPointClouds.length > 0,
          disabled: !this.viewer || (!this.closedPointClouds.length && !this.shownPointClouds.length),
          toggle: 'showPointCloud',
        },
        {
          id: 'ghost',
          icon: 'mdi-ghost-outline',
          label: 'Ghosting',
          active: this.isGhostingOn,
        },
        {
          id: 'visibility',
          icon: 'mdi-eye-settings',
          label: 'Visibility Range',
          active: true,
          hasSlider: true,
          sliderConfig: {
            label: 'Visibility Range',
            unit: 'm',
            min: 1,
            max: 100,
            logScale: true,
          },
        },
        {
          id: 'pcSettings',
          icon: 'mdi-cog-sync',
          label: 'Point Cloud Settings',
          active: false,
          hasAction: true,
          action: 'openPointCloudSettings',
        },
      ];
      // Focus-mode toggle — shown only while a master tag info panel is open
      // (the only time the doc-viewer link isolation applies). Placed next to the
      // Visibility Range button. See docLinkIsolationMixin.
      if (this.$store.state.TAGS && this.$store.state.TAGS.isTagFocusContextActive) {
        const focusItem = {
          id: 'focusMode',
          icon: 'mdi-target',
          label: 'Focus',
          active: this.displaySettings?.focusMode,
          toggle: 'focusMode',
        };
        const visIdx = items.findIndex((i) => i.id === 'visibility');
        items.splice(visIdx === -1 ? items.length : visIdx, 0, focusItem);
      }
      return items;
    },

    sliderValue: {
      get() {
        if (this.activeSlider?.id === 'visibility') {
          return this.displaySettings?.visibilityRange || 10;
        }
        return 0;
      },
      set(value) {
        if (this.activeSlider?.id === 'visibility') {
          this.setVisibilityRange(value);
        }
      },
    },

    // ── Ghosting state ─────────────────────────────────────────────────────
    // These flags are plain root-store state that predates the mutation convention
    // and is written the same way across the app (displaySettings, pageView). We
    // mirror that established computed-setter pattern rather than split one piece of
    // state across two write styles. NOTE: the colour/opacity material math duplicates
    // displaySettings — a candidate to consolidate into forgeService later.
    isGhostingOn: {
      get() {
        return this.$store.state.isGhostingOn;
      },
      set(value) {
        this.$store.state.isGhostingOn = value;
        this.forgeService?.updateGhosting();
      },
    },
    ghostWireframe: {
      get() {
        return this.$store.state.isGhostingWireframe;
      },
      set(value) {
        this.$store.state.isGhostingWireframe = value;
        this.forgeService?.updateGhosting();
      },
    },
    ghostColor: {
      get() {
        return this.$store.state.ghostingColor;
      },
      set(value) {
        this.$store.state.ghostingColor = value;
        const mat = this.forgeService?.ghostingMaterial;
        // Read THREE at call time — this component mounts with the layout, possibly
        // before the Forge viewer publishes window.THREE (a module-load capture could
        // be permanently undefined).
        const THREE = window.THREE;
        if (mat && this.viewer && THREE) {
          mat.color = new THREE.Color(value);
          this.viewer.impl.invalidate(true, true, true);
        }
      },
    },
    ghostOpacity: {
      get() {
        return this.$store.state.ghostingOpacity;
      },
      set(value) {
        this.$store.state.ghostingOpacity = value;
        const mat = this.forgeService?.ghostingMaterial;
        if (mat && this.viewer) {
          mat.opacity = value / 100;
          this.viewer.impl.invalidate(true, true, true);
        }
      },
    },
    ghostOverlayOpenRequest() {
      return this.$store.getters['GENERAL/GET_GHOST_OVERLAY_OPEN_REQUEST'];
    },
    // Reactive gate for the On/Off action. Reads the TRUE isolation source — the main
    // extension's isolatedDbids — via a live count polled while the overlay is open
    // (startIsolationPoll). We deliberately do NOT gate on store.isIsolating3D: that flag is
    // not reliably cleared (mainExtension.endIsolation() empties isolatedDbids without
    // resetting it), so it can still read "isolated" when nothing is. (Codex review fix.)
    ghostHasIsolation() {
      return this.ghostIsolatedCount > 0;
    },
  },

  methods: {
    close() {
      this.$store.commit('GENERAL/SET_QUICK_SETTINGS_VISIBLE', false);
      this.closeSlider();
      this.closeSubItemsOverlay();
      this.closeGhostOverlay();
      this.$emit('close');
    },

    togglePin() {
      const nextPinned = !this.isPinPreferenceActive;
      this.closeSubItemsOverlay();
      this.closeSlider();
      this.closeGhostOverlay();
      this.$store.commit('GENERAL/SET_QUICK_SETTINGS_PINNED', nextPinned);
    },

    constrainToViewport(pos) {
      const bounds = this.viewerAreaBounds;
      const dims = this.getBarDimensions();
      const margin = bounds.margin;

      const minX = bounds.leftOffset + margin;
      const maxX = Math.max(minX, window.innerWidth - bounds.rightOffset - dims.width - margin);
      const minY = bounds.topOffset + margin;
      const maxY = Math.max(minY, window.innerHeight - bounds.bottomOffset - dims.height - margin);

      const posX = (pos && typeof pos.x === 'number') ? pos.x : bounds.defaultX;
      const posY = (pos && typeof pos.y === 'number') ? pos.y : bounds.defaultY;

      return {
        x: Math.max(minX, Math.min(posX, maxX)),
        y: Math.max(minY, Math.min(posY, maxY)),
      };
    },

    getBarDimensions() {
      if (this.$el && this.$el.offsetWidth) {
        return {
          width: this.$el.offsetWidth,
          height: this.$el.offsetHeight || 52,
        };
      }
      return { width: 620, height: 52 };
    },

    // ========================================================================
    // Tag Item State Calculation
    // ========================================================================

    /**
     * Compute the visual state for a tag item button
     * @param {Object} item - Tag item configuration
     * @returns {'off'|'partial'|'full'} Visual state for CSS styling
     */
    getItemState(item) {
      if (!item.children) {
        // Simple button - check its flags
        const isActive = item.flags?.some(flag => this.displaySettings?.[flag]);
        return isActive ? 'full' : 'off';
      }

      // Parent button - check children
      const activeCount = item.children.filter(
        child => this.displaySettings?.[child.flag]
      ).length;

      if (activeCount === 0) return 'off';
      if (activeCount === item.children.length) return 'full';
      return 'partial';
    },

    // ========================================================================
    // Tag Click Handlers
    // ========================================================================

    /**
     * Handle left-click on tag item
     * - Parent buttons: Toggle ALL children ON/OFF
     * - Simple buttons: Toggle their flags
     */
    handleTagLeftClick(item, event) {
      if (item.disabled) return;
      if (!this.displaySettings) return;

      // Close sub-items overlay if open
      this.closeSubItemsOverlay();

      if (item.children) {
        // Parent button: toggle ALL children
        const anyActive = item.children.some(c => this.displaySettings?.[c.flag]);
        const targetState = !anyActive;

        item.children.forEach(child => {
          this.displaySettings[child.flag] = targetState;
        });
      } else {
        // Simple button: toggle its flags
        item.flags?.forEach(flag => {
          this.displaySettings[flag] = !this.displaySettings[flag];
        });
      }

      // Update viewer
      this.forgeService?.update3dIconsInAllViews();

      // CML + TML markers use a separate rendering path — sync them if this
      // parent has a CML or TML child (the Inspection group has both).
      if (item.children?.some(c => c.flag === 'showCml')) {
        this.syncCmlMarkers();
      }
      if (item.children?.some(c => c.flag === 'showTml')) {
        this.syncTmlMarkers();
      }
    },

    /**
     * Handle right-click on tag item (context menu)
     * - Opens sub-items overlay for parent buttons
     * - No action for simple buttons
     */
    handleTagRightClick(item, event) {
      if (!item.children) return;

      // Calculate overlay position relative to container
      const button = event.target.closest('.qs-icon');
      if (!button) return;

      const rect = button.getBoundingClientRect();
      const containerRect = this.$el.getBoundingClientRect();

      this.subItemsPosition = {
        x: rect.left - containerRect.left + rect.width / 2,
        y: rect.bottom - containerRect.top + (this.pinned ? 6 : 8),
      };

      // Toggle overlay (close if same item clicked again)
      if (this.activeSubItems?.id === item.id) {
        this.closeSubItemsOverlay();
      } else {
        this.activeSubItems = item;
      }
    },

    // ========================================================================
    // Sub-Items Overlay Handlers
    // ========================================================================

    /**
     * Handle toggle from sub-items overlay
     */
    handleSubItemToggle(subItem) {
      if (!this.displaySettings) return;

      this.displaySettings[subItem.flag] = !this.displaySettings[subItem.flag];
      this.forgeService?.update3dIconsInAllViews();

      // CML / TML markers use a separate rendering path not handled by
      // update3dIconsInAllViews. Branch on the toggled flag.
      if (subItem.flag === 'showCml') {
        this.syncCmlMarkers();
      } else if (subItem.flag === 'showTml') {
        this.syncTmlMarkers();
      }
    },

    /**
     * Handle "All" / "None" button in sub-items overlay
     */
    handleSubItemToggleAll(targetState) {
      if (!this.activeSubItems?.children || !this.displaySettings) return;

      this.activeSubItems.children.forEach(child => {
        this.displaySettings[child.flag] = targetState;
      });
      this.forgeService?.update3dIconsInAllViews();

      // CML + TML markers use a separate rendering path — sync both if this
      // parent has either child (the Inspection group does).
      if (this.activeSubItems?.children?.some(c => c.flag === 'showCml')) {
        this.syncCmlMarkers();
      }
      if (this.activeSubItems?.children?.some(c => c.flag === 'showTml')) {
        this.syncTmlMarkers();
      }
    },

    /**
     * Handle CML marker visibility based on current showCml flag.
     * CML uses a separate rendering path (Three.js spheres) not handled by update3dIconsInAllViews.
     *
     * Re-show reads the panel-fetched CMLs from the monitoring cache
     * (`getInspectionCmls`), NOT `activeInspection.cmlRecords` — the inspection
     * DTO in GET_EDITING_AMIS_TAG always carries an empty cmlRecords, so the old
     * check never re-rendered. showInspectionCmlTmlMarkers populates the cache
     * on panel open.
     */
    syncCmlMarkers() {
      if (this.displaySettings?.showCml) {
        const activeInspection = this.$store.getters['GET_EDITING_AMIS_TAG'];
        const cmls = getInspectionCmls(activeInspection?.id);
        if (cmls.length) {
          // Worst-first marker budget — getInspectionCmls returns the FULL cached set;
          // cap the RENDER subset (deterministic sort → same subset syncTmlMarkers picks). (AVA-437)
          this.forgeService?.showCmlPointMarkers({ cmlRecords: budgetCmlsForMarkers(cmls).cmls });
        }
      } else {
        this.forgeService?.clearCmlPointMarkers();
      }
    },

    /**
     * Handle TmlPoint marker visibility based on current showTml flag.
     * Parallel to syncCmlMarkers — TML uses its own rendering path
     * (`tml-markers-scene` in forgeService.js) not handled by update3dIconsInAllViews.
     *
     * Re-show reads the panel-fetched CMLs from cache (`getInspectionCmls`) and
     * hydrates their TmlPoints (`hydrateCmlsWithTmlPoints`). Both the CMLs and
     * the TmlPoints were loaded into the cache by showInspectionCmlTmlMarkers on
     * panel open — so toggle-off-then-on restores TML. Tag-info-panel
     * (master-tag) marker rendering lives in tagInfoCmlMarkersMixin.js and
     * survives QuickSettings toggles via its own visibilitySnapshot watcher.
     */
    syncTmlMarkers() {
      if (this.displaySettings?.showTml) {
        const activeInspection = this.$store.getters['GET_EDITING_AMIS_TAG'];
        const cmls = getInspectionCmls(activeInspection?.id);
        if (cmls.length) {
          // Same worst-first budget as syncCmlMarkers so the TML layer caps to the SAME
          // CML subset the CML layer renders (deterministic sort → identical subset). (AVA-437)
          this.forgeService?.showTmlPointMarkers({
            cmlRecords: hydrateCmlsWithTmlPoints(budgetCmlsForMarkers(cmls).cmls),
          });
        }
      } else {
        this.forgeService?.clearTmlPointMarkers();
      }
    },

    closeSubItemsOverlay() {
      this.activeSubItems = null;
    },

    // ========================================================================
    // View Item Handlers (unchanged)
    // ========================================================================

    handleToggle(item, event) {
      if (item.disabled) return;

      if (item.id === 'ghost') {
        this.toggleGhostOverlay(event);
        return;
      }

      if (item.hasAction) {
        this.executeAction(item);
        return;
      }

      if (item.hasSlider) {
        // Slider items don't toggle, they open slider
        return;
      }

      // Execute toggle
      this.executeToggle(item);
    },

    handleSliderToggle(item, event) {
      if (this.activeSlider?.id === item.id) {
        this.closeSlider();
      } else {
        this.openSlider(item, event);
      }
    },

    openSlider(item, event) {
      const rect = event.target.getBoundingClientRect();
      const containerRect = this.$el.getBoundingClientRect();

      this.sliderPosition = {
        x: rect.left - containerRect.left + rect.width / 2,
        y: rect.bottom - containerRect.top + (this.pinned ? 6 : 8),
      };

      this.activeSlider = {
        id: item.id,
        ...item.sliderConfig,
      };
    },

    closeSlider() {
      this.activeSlider = null;
    },

    handleSliderInput(value) {
      this.sliderValue = value;
    },

    executeToggle(item) {
      if (!this.displaySettings) return;

      switch (item.toggle) {
        case 'focusMode': {
          // Doc-viewer link isolation preference. Toggle + persist (per-device).
          // The open master tag panel's docLinkIsolationMixin watches this flag
          // and applies/removes the fade live — no 3D redraw needed here.
          const enabled = !this.displaySettings.focusMode;
          this.displaySettings.focusMode = enabled;
          window.localStorage.setItem(btoa('focusMode'), String(enabled));
          return;
        }
        case 'show360':
          this.displaySettings.show360 = !this.displaySettings.show360;
          this.$store.state.projects?.forEach((o) =>
            o.images?.forEach((i) => (i.isVisibleInViewers = this.displaySettings.show360)),
          );
          break;
        case 'showMasterModel':
          this.displaySettings.showMasterModel = !this.displaySettings.showMasterModel;
          if (this.forgeService?.getMainExtension() && this.viewer) {
            setModelVisibility(this.viewer, this.viewer.model, this.displaySettings.showMasterModel);
          }
          break;
        case 'showPointCloud':
          this.togglePointCloud();
          break;
      }

      // Update viewer
      this.forgeService?.update3dIconsInAllViews();
    },

    togglePointCloud() {
      const isShowing = this.shownPointClouds.length > 0;

      if (isShowing) {
        // Hide all point clouds
        this.closedPointClouds = [...this.shownPointClouds];
        this.shownPointClouds.forEach(p => {
          this.forgeService?.setShowingPointCloudIn3D(p, false);
        });
      } else {
        // Show previously closed point clouds
        this.closedPointClouds.forEach(p => {
          this.forgeService?.setShowingPointCloudIn3D(p, true);
        });
        this.closedPointClouds = [];
      }
    },

    setVisibilityRange(value) {
      if (!this.displaySettings) return;

      this.displaySettings.visibilityRange = value;

      if (this.forgeService?.getLabels3DExtension()) {
        this.forgeService.getLabels3DExtension().updateVisibilityRange();
      }
      this.forgeService?.updateTagsAnnotationsInPanoViewer();
      this.forgeService?.update360ImagesHotspots();
    },

    executeAction(item) {
      if (item.action === 'openPointCloudSettings') {
        this.$store.commit('SET_POINT_CLOUDS_POPUP_VISIBLE', true);
      }
    },

    // ── Ghost-style overlay ────────────────────────────────────────────────
    toggleGhostOverlay(event) {
      if (this.ghostOverlayOpen) {
        this.closeGhostOverlay();
      } else {
        this.openGhostOverlay(event);
      }
    },

    openGhostOverlay(event) {
      this.refreshGhostIsolatedCount();
      this.startIsolationPoll();
      this.ghostOverlayPosition = this.overlayPositionFor(this.resolveGhostIconEl(event));
      this.closeSlider();
      this.closeSubItemsOverlay();
      this.ghostOverlayOpen = true;
    },

    closeGhostOverlay() {
      this.ghostOverlayOpen = false;
      this.stopIsolationPoll();
    },

    // Refresh the cached isolation count from the main extension — the TRUE source
    // (isolatedDbids is a plain, non-reactive field). Cheap: a single length check.
    refreshGhostIsolatedCount() {
      const ext = this.forgeService?.getMainExtension?.();
      this.ghostIsolatedCount = ext?.isolatedDbids?.length || 0;
    },

    // Poll the isolation count only while the overlay is open. isolatedDbids is non-reactive
    // and the store isIsolating3D flag is unreliable (mainExtension.endIsolation empties
    // isolatedDbids without clearing it), so read the real source on a short interval —
    // bounded to the panel's open lifetime and cleared on close / destroy.
    startIsolationPoll() {
      this.stopIsolationPoll();
      this._isolationPoll = window.setInterval(() => this.refreshGhostIsolatedCount(), 300);
    },

    stopIsolationPoll() {
      if (this._isolationPoll) {
        window.clearInterval(this._isolationPoll);
        this._isolationPoll = null;
      }
    },

    // Center an overlay under a given strip-icon element, in coordinates relative to the
    // floating bar (this.$el). Falls back to a sane bar-center default if the element is
    // missing (e.g. the ghost icon isn't in the DOM yet).
    overlayPositionFor(el) {
      if (!el || !this.$el) return { x: 280, y: this.pinned ? 40 : 52 };
      const rect = el.getBoundingClientRect();
      const containerRect = this.$el.getBoundingClientRect();
      return {
        x: rect.left - containerRect.left + rect.width / 2,
        y: rect.bottom - containerRect.top + (this.pinned ? 6 : 8),
      };
    },

    // Resolve the ghost strip-icon element. On click, prefer the actually-clicked icon (the
    // proven pattern used by SliderOverlay / TagSubItemsOverlay); on auto-open, which carries
    // no click event, query the DOM for the icon's glyph. Both avoid template refs — a dynamic
    // :ref inside this v-for resolves unreliably in Vue 2.
    resolveGhostIconEl(event) {
      const fromClick = event?.target?.closest?.('.qs-icon');
      if (fromClick) return fromClick;
      // Auto-open path (no click event): find the ghost icon by its glyph within the bar.
      const glyph = this.$el?.querySelector('.qs-icon .mdi-ghost-outline');
      return glyph?.closest('.qs-icon') || null;
    },

    onGhostToggle() {
      // Never let "ghost on" grey the whole model when nothing is isolated — refresh the
      // true source at click time, independent of the poll tick.
      this.refreshGhostIsolatedCount();
      if (!this.isGhostingOn && !this.ghostHasIsolation) return;
      this.isGhostingOn = !this.isGhostingOn;
    },

    onGhostSetWireframe(value) {
      this.ghostWireframe = value;
    },

    onGhostSetColor(value) {
      this.ghostColor = value;
    },

    onGhostSetOpacity(value) {
      this.ghostOpacity = value;
    },

    onGhostSetAutoShow(value) {
      this.ghostAutoShow = value;
      writeGhostAutoShow(value);
    },

    // Consume the layout's one-shot "open the ghost overlay" request (set when ghosting
    // turned on while the bar was hidden, or from any external trigger).
    consumeGhostOpenRequest() {
      if (!this.ghostOverlayOpenRequest) return;
      this.$store.commit('GENERAL/SET_GHOST_OVERLAY_OPEN_REQUEST', false);
      // Already open (e.g. the user toggled ghosting On from inside this overlay) → leave it
      // in place; re-opening would recompute the position and make it appear to jump.
      if (this.ghostOverlayOpen) return;
      this.$nextTick(() => this.openGhostOverlay());
    },
  },

  mounted() {
    this.ghostAutoShow = readGhostAutoShow();
    // The bar may have just mounted because ghosting turned on — honor a pending request.
    this.consumeGhostOpenRequest();

    // Initial position setup for floating mode: ensure position is clamped and centered within viewer area
    if (!this.pinned && this.draggableMixinState) {
      const hasLoaded = this.loadDragPosition();
      if (!hasLoaded) {
        this.draggableMixinState.position = {
          x: this.viewerAreaBounds.defaultX,
          y: this.viewerAreaBounds.defaultY,
        };
        this.saveDragPosition();
      } else {
        const constrained = this.constrainToViewport(this.draggableMixinState.position);
        if (constrained.x !== this.draggableMixinState.position.x || constrained.y !== this.draggableMixinState.position.y) {
          this.draggableMixinState.position = constrained;
          this.saveDragPosition();
        }
      }
    }
  },

  beforeDestroy() {
    // Stop the isolation poll if the bar is torn down while the overlay is open.
    this.stopIsolationPoll();
  },

  watch: {
    // Bar already visible + ghosting turns on → the layout flips this request flag.
    ghostOverlayOpenRequest(requested) {
      if (requested) this.consumeGhostOpenRequest();
    },
    viewerAreaBounds: {
      handler() {
        if (this.pinned || !this.draggableMixinState?.position) return;
        const current = this.draggableMixinState.position;
        const constrained = this.constrainToViewport(current);
        if (constrained.x !== current.x || constrained.y !== current.y) {
          this.draggableMixinState.position = constrained;
          this.saveDragPosition();
        }
      },
      deep: true,
    },
  },
};
</script>

<style lang="scss" scoped>
// ============================================================================
// Quick Settings Floating - Compact Single-Row Design
// With Light/Dark Theme Support
// ============================================================================

.qs-floating {
  position: fixed;
  z-index: 100;
  user-select: none;

  // Dragging state
  &.is-dragging {
    cursor: grabbing !important;
    * {
      cursor: grabbing !important;
    }
  }
}

// ============================================================================
// Quick Settings Pinned - Top Bar Integration (Option B: Frameless Flow)
// ============================================================================

.qs-pinned {
  position: relative;
  display: flex;
  align-items: center;
  user-select: none;
  z-index: 100;
  height: 100%;

  .qs-container {
    display: flex;
    align-items: center;
    padding: 0 !important;
    height: 36px !important;
    background: transparent !important;
    border: none !important;
    box-shadow: none !important;
    backdrop-filter: none !important;
  }

  .qs-icon-strip {
    display: flex;
    align-items: center;
    gap: 3px;
    padding: 0;
  }

  .qs-icon-group {
    display: flex;
    align-items: center;
    gap: 3px;
  }

  ::v-deep .qs-icon {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px !important;
    height: 36px !important;
    min-width: 36px !important;
    max-width: 36px !important;
    min-height: 36px !important;
    max-height: 36px !important;
    padding: 0 !important;
    margin: 0 !important;
    border-radius: 4px 4px 0 0 !important;
    background: transparent !important;
    border: none !important;
    // Symmetrical 2px top & bottom borders guarantee 100% dead-centered icon
    border-top: 2px solid transparent !important;
    border-bottom: 2px solid transparent !important;
    box-shadow: none !important;
    outline: none !important;
    opacity: 1 !important;
    transform: none !important;
    filter: none !important;
    transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;

    // INACTIVE / DEFAULT STATE (ALL buttons: Tags and View items)
    .qs-icon-inner {
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      width: 100% !important;
      height: 100% !important;
      margin: 0 !important;
      padding: 0 !important;
    }

    .qs-icon-inner .v-icon,
    .v-icon {
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      font-size: 20px !important;
      line-height: 1 !important;
      margin: 0 !important;
      padding: 0 !important;
      color: #e2e8f0 !important;
      opacity: 1 !important;
      transition: color 0.15s ease;
    }

    // Hover on inactive button
    &:hover,
    &:hover:not(.is-disabled) {
      background: rgba(255, 255, 255, 0.08) !important;
      transform: none !important;
      filter: none !important;
      box-shadow: none !important;

      .qs-icon-inner .v-icon,
      .v-icon {
        color: #ffffff !important;
      }
    }

    &:active,
    &:active:not(.is-disabled) {
      transform: none !important;
      filter: none !important;
      box-shadow: none !important;
    }

    // STATE OFF (Clean slate, no dashed boxes, no dark tints)
    &.state-off {
      background: transparent !important;
      border: none !important;
      border-top: 2px solid transparent !important;
      border-bottom: 2px solid transparent !important;
      box-shadow: none !important;
      opacity: 1 !important;

      .qs-icon-inner .v-icon,
      .v-icon {
        color: #e2e8f0 !important;
        opacity: 1 !important;
      }

      &:hover,
      &:hover:not(.is-disabled) {
        background: rgba(255, 255, 255, 0.08) !important;
        border: none !important;
        border-top: 2px solid transparent !important;
        border-bottom: 2px solid transparent !important;
        box-shadow: none !important;
        transform: none !important;

        .qs-icon-inner .v-icon,
        .v-icon {
          color: #ffffff !important;
        }
      }
    }

    // ========================================================================
    // ACTIVE VIEW TOGGLES (360, 3D Model, Point Clouds, Ghost, Visibility, PC Settings)
    // ========================================================================
    &.is-active {
      border-bottom: 2px solid #61b695 !important;
      background: transparent !important;

      .qs-icon-inner .v-icon,
      .v-icon {
        color: #61b695 !important;
      }

      &:hover,
      &:hover:not(.is-disabled) {
        background: rgba(255, 255, 255, 0.08) !important;
        .qs-icon-inner .v-icon,
        .v-icon {
          color: #61b695 !important;
        }
      }
    }

    // ========================================================================
    // ACTIVE TAG CATEGORIES (Operation, Inspection, Equipment, Pipeline, Structural)
    // ========================================================================

    // 1. Operation Tag (Purple)
    &.qs-tag-operation,
    &[style*="#665b9b" i] {
      &.state-full {
        border-bottom: 2px solid #7c6bb5 !important;
        .qs-icon-inner .v-icon, .v-icon { color: #c4b5fd !important; }
      }
      &.state-partial {
        border-bottom: 2px dashed #7c6bb5 !important;
        .qs-icon-inner .v-icon, .v-icon { color: #c4b5fd !important; }
      }
      &.state-full:hover,
      &.state-partial:hover {
        background: rgba(255, 255, 255, 0.08) !important;
        .qs-icon-inner .v-icon, .v-icon { color: #c4b5fd !important; }
      }
    }

    // 2. Inspection Tag (Maroon)
    &.qs-tag-inspection,
    &[style*="#9a5454" i] {
      &.state-full {
        border-bottom: 2px solid #b86262 !important;
        .qs-icon-inner .v-icon, .v-icon { color: #fca5a5 !important; }
      }
      &.state-partial {
        border-bottom: 2px dashed #b86262 !important;
        .qs-icon-inner .v-icon, .v-icon { color: #fca5a5 !important; }
      }
      &.state-full:hover,
      &.state-partial:hover {
        background: rgba(255, 255, 255, 0.08) !important;
        .qs-icon-inner .v-icon, .v-icon { color: #fca5a5 !important; }
      }
    }

    // 3. Equipment Tag (Cyan)
    &.qs-tag-equipment,
    &[style*="#00a8b7" i] {
      &.state-full {
        border-bottom: 2px solid #00a8b7 !important;
        .qs-icon-inner .v-icon, .v-icon { color: #67e8f9 !important; }
      }
      &.state-partial {
        border-bottom: 2px dashed #00a8b7 !important;
        .qs-icon-inner .v-icon, .v-icon { color: #67e8f9 !important; }
      }
      &.state-full:hover,
      &.state-partial:hover {
        background: rgba(255, 255, 255, 0.08) !important;
        .qs-icon-inner .v-icon, .v-icon { color: #67e8f9 !important; }
      }
    }

    // 4. Pipeline Tag (Steel Blue)
    &.qs-tag-pipeline,
    &[style*="#547d9a" i] {
      &.state-full {
        border-bottom: 2px solid #547d9a !important;
        .qs-icon-inner .v-icon, .v-icon { color: #93c5fd !important; }
      }
      &.state-partial {
        border-bottom: 2px dashed #547d9a !important;
        .qs-icon-inner .v-icon, .v-icon { color: #93c5fd !important; }
      }
      &.state-full:hover,
      &.state-partial:hover {
        background: rgba(255, 255, 255, 0.08) !important;
        .qs-icon-inner .v-icon, .v-icon { color: #93c5fd !important; }
      }
    }

    // 5. Structural Tag (Olive)
    &.qs-tag-structural,
    &[style*="#5f6f64" i] {
      &.state-full {
        border-bottom: 2px solid #6b8273 !important;
        .qs-icon-inner .v-icon, .v-icon { color: #86efac !important; }
      }
      &.state-partial {
        border-bottom: 2px dashed #6b8273 !important;
        .qs-icon-inner .v-icon, .v-icon { color: #86efac !important; }
      }
      &.state-full:hover,
      &.state-partial:hover {
        background: rgba(255, 255, 255, 0.08) !important;
        .qs-icon-inner .v-icon, .v-icon { color: #86efac !important; }
      }
    }

    // Generic fallback for state-full / state-partial
    &.state-full {
      border-bottom: 2px solid var(--tag-color, #61b695) !important;
      background: transparent !important;
      .qs-icon-inner .v-icon, .v-icon { color: var(--tag-color, #61b695) !important; }
    }
    &.state-partial {
      border-bottom: 2px dashed var(--tag-color, #61b695) !important;
      background: transparent !important;
      .qs-icon-inner .v-icon, .v-icon { color: var(--tag-color, #61b695) !important; }
    }

    // Preserve children dropdown indicator for hierarchical tags
    &.has-children::after {
      content: '';
      position: absolute;
      bottom: 3px;
      right: 3px;
      width: 0;
      height: 0;
      border-left: 3px solid transparent;
      border-right: 3px solid transparent;
      border-top: 3px solid currentColor;
      opacity: 0.45;
      transition: opacity 0.2s ease;
      display: block !important;
    }

    &.has-children:hover::after {
      opacity: 0.8;
    }

    // Slider indicator for items with slider
    .qs-slider-indicator {
      display: block !important;
      position: absolute;
      bottom: 3px;
      left: 50%;
      transform: translateX(-50%);
      width: 12px;
      height: 2px;
      border-radius: 1px;
      background: currentColor !important;
      opacity: 0.4;
    }

    &:hover .qs-slider-indicator {
      opacity: 0.8;
    }

    // Hide extra glow rings in pinned mode
    .qs-icon-glow,
    .qs-action-indicator {
      display: none !important;
    }
  }

  // Dividers between groups: 1px x 20px rgba(255, 255, 255, 0.25)
  .qs-divider {
    width: 1px !important;
    height: 20px !important;
    margin: 0 6px !important;
    background: rgba(255, 255, 255, 0.25) !important;
    opacity: 1 !important;

    &--subtle {
      display: none !important;
    }
  }

  // Pin Button: 36x36px, 20px icon font size, mint glyph #61b695, hover rgba(97, 182, 149, 0.2)
  .qs-actions-group {
    display: flex;
    align-items: center;
    padding-left: 0;

    .qs-action-btn {
      position: relative;
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      width: 36px !important;
      height: 36px !important;
      min-width: 36px !important;
      max-width: 36px !important;
      min-height: 36px !important;
      max-height: 36px !important;
      padding: 0 !important;
      margin: 0 !important;
      border-radius: 4px !important;
      border: none !important;
      background: transparent !important;
      box-shadow: none !important;
      outline: none !important;
      transform: none !important;
      transition: background 0.15s ease, color 0.15s ease;

      .v-icon {
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        width: 20px !important;
        height: 20px !important;
        font-size: 20px !important;
        line-height: 1 !important;
        margin: 0 !important;
        padding: 0 !important;
        color: #61b695 !important;
        transition: color 0.15s ease;
      }

      &:hover {
        background: rgba(97, 182, 149, 0.2) !important;
        transform: none !important;

        .v-icon {
          color: #61b695 !important;
        }
      }

      &:active {
        transform: none !important;
      }
    }
  }
}

// ============================================================================
// Actions Group (Pin Button)
// ============================================================================

.qs-actions-group {
  display: flex;
  align-items: center;
  gap: 3px;
  padding-left: 2px;
}

.qs-action-btn {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: var(--ds-radius-md);
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.16, 1, 0.3, 1);
  outline: none;
  -webkit-appearance: none;
  appearance: none;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.03);

  .v-icon {
    color: rgba(255, 255, 255, 0.6);
    transition: color 0.15s ease;
  }

  .theme--light & {
    border: 1px solid rgba(0, 0, 0, 0.08);
    background: rgba(0, 0, 0, 0.02);

    .v-icon {
      color: rgba(0, 0, 0, 0.5);
    }
  }

  &:hover {
    transform: translateY(-1px);
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.2);

    .v-icon {
      color: rgba(255, 255, 255, 0.95) !important;
    }

    .theme--light & {
      background: rgba(0, 0, 0, 0.08);
      border-color: rgba(0, 0, 0, 0.2);

      .v-icon {
        color: rgba(0, 0, 0, 0.85) !important;
      }
    }
  }

  &:active {
    transform: translateY(0);
  }

  &.is-pinned-btn {
    background: rgba(97, 182, 149, 0.15);
    border-color: rgba(97, 182, 149, 0.35);

    .v-icon {
      color: #61b695 !important;
    }
  }
}

// ============================================================================
// Main Container - Compact horizontal layout
// ============================================================================

.qs-container {
  display: flex;
  align-items: center;
  padding: 6px 8px;
  gap: 0;
  border-radius: var(--ds-radius-lg);
  backdrop-filter: blur(20px);

  // Dark theme (default)
  background: linear-gradient(
    180deg,
    rgba(30, 35, 40, 0.96) 0%,
    rgba(22, 26, 30, 0.96) 100%
  );
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow:
    0 4px 20px rgba(0, 0, 0, 0.4),
    0 1px 4px rgba(0, 0, 0, 0.2),
    inset 0 1px 0 rgba(255, 255, 255, 0.05);

  // Light theme
  .theme--light & {
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.98) 0%,
      rgba(248, 250, 252, 0.98) 100%
    );
    border: 1px solid rgba(0, 0, 0, 0.1);
    box-shadow:
      0 4px 20px rgba(0, 0, 0, 0.12),
      0 1px 4px rgba(0, 0, 0, 0.06),
      inset 0 1px 0 rgba(255, 255, 255, 0.8);
  }
}

// ============================================================================
// Drag Zone - Left side grip
// ============================================================================

.qs-drag-zone {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 6px;
  cursor: grab;
  border-radius: var(--ds-radius-sm);
  margin-right: 4px;
  transition: background 0.15s ease;

  &:hover {
    // Dark theme
    background: rgba(255, 255, 255, 0.08);

    .theme--light & {
      background: rgba(0, 0, 0, 0.06);
    }

    .drag-icon {
      color: rgba(255, 255, 255, 0.6) !important;

      .theme--light & {
        color: rgba(0, 0, 0, 0.5) !important;
      }
    }
  }

  &:active {
    cursor: grabbing;
  }
}

.drag-icon {
  transition: color 0.15s ease;

  // Dark theme
  color: rgba(255, 255, 255, 0.3) !important;

  // Light theme
  .theme--light & {
    color: rgba(0, 0, 0, 0.3) !important;
  }
}

// ============================================================================
// Icon Strip - Center section
// ============================================================================

.qs-icon-strip {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 0 4px;
}

.qs-icon-group {
  display: flex;
  align-items: center;
  gap: 4px;
}

.qs-divider {
  width: 1px;
  height: 28px;
  margin: 0 8px;

  // Dark theme
  background: rgba(255, 255, 255, 0.12);

  // Light theme
  .theme--light & {
    background: rgba(0, 0, 0, 0.1);
  }

  // Subtle variant for internal tag group separation
  &--subtle {
    height: 20px;
    margin: 0 4px;
    opacity: 0.5;
  }
}

// ============================================================================
// Transitions
// ============================================================================

.qs-float-enter-active {
  animation: qsSlideIn 0.2s cubic-bezier(0.16, 1, 0.3, 1);
}

.qs-float-leave-active {
  animation: qsSlideOut 0.12s cubic-bezier(0.5, 0, 0.75, 0);
}

@keyframes qsSlideIn {
  from {
    opacity: 0;
    transform: translateY(-8px) scale(0.97);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes qsSlideOut {
  from {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
  to {
    opacity: 0;
    transform: translateY(-4px) scale(0.98);
  }
}
</style>
